<template>
  <modal :name="`shortlist-${listingId}`" @closed="closeModal" :scrollable="true" height="auto">
    <div class="modal-body">
      <button aria-label="Close" class="inline-block close" type="button" @click.prevent="closeModal">
        <span aria-hidden="true">×</span>
      </button>

      <header class="flex my-6 justify-between items-center">
        <div class="flex">
          <div class="mr-6" v-if="preview">
            <img src="@/images/icons/bouncing-ball.svg" class="w-20" v-show="!previewImgLoaded" />
            <img :src="preview" class="inline-block w-20" @load="imageLoaded" v-show="previewImgLoaded" />
          </div>
          <h2 class="text-2xl mb-6">Shortlist Entry</h2>
        </div>

        <div class="flex-col">
          <div :style="categoryStyle" class="flex justify-between px-4 py-3 flex text-white rounded-full">
            <strong>{{ categoryTitle }}</strong>
            <button @click.prevent="showCategorySelect = !showCategorySelect" class="ml-2">
              <FaIcon :icon="['far', 'pencil']" />
            </button>
          </div>
        </div>
      </header>

      <div class="flex">
        <div class="w-1/2 mr-6">
          <Select
            className="shortlist__select text-base bg-white pl-0"
            placeholder="Search or create a tag"
            name="tags"
            :clearable="true"
            :value="selectedTag"
            :options="selectableTags"
            :taggable="true"
            @change="onChange"
            :reduce="(option) => option"
            :selectable="(option) => !changedTags.includes(option)"
          />
        </div>

        <div class="w-1/2">
          <transition name="fade">
            <Select
              v-if="showCategorySelect"
              className="shortlist__select bg-white pl-0 text-base"
              placeholder="Select a new category"
              name="categories"
              :clearable="true"
              v-model="category"
              :searchable="false"
              :options="categories"
              label="title"
              :reduce="(options) => options.id"
            >
            </Select>
          </transition>
        </div>
      </div>
      <div class="flex">
        <div class="w-full">
          <h3 class="text-xl my-6">Selected Tags</h3>

          <div class="shortlist__tag-list bg-grey-lighter pl-6 py-8 w-full _scrollbar--hidden">
            <ul v-if="selectableTags.length">
              <ShortlistTag
                v-for="(tag, index) in changedTags"
                :tag="tag"
                :key="index"
                :results="results"
                @add="add(tag)"
                @remove="remove(tag)"
              />
            </ul>
            <div class="flex items-baseline" v-else>
              <p class="mr-4">Loading tags</p>
              <FaIcon :icon="['fal', 'spinner-third']" class="fa-spin" />
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center mt-6">
        <button class="btn btn-primary shortlist__create-btn" @click.prevent="save">Apply Changes</button>
        <span v-if="saving" class="ml-4"><FaIcon :icon="['fas', 'sync-alt']" class="fa-spin" /></span>
        <div v-if="error" class="text-red-600 ml-6">{{ error }}</div>
      </div>
    </div>
  </modal>
</template>

<script>
import Http from "@/lib/Http"
import ShortlistTag from "./ShortlistTag.vue"
import Select from "@/components/Form/Select.vue"
const closeTimeout = 500
export default {
  props: {
    listingId: {
      type: [String, Number],
      required: true,
    },
    apiUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selectedTag: "",
      selectableTags: [],
      changedTags: [],
      saving: false,
      previewImgLoaded: false,
      preview: null,
      results: [],
      categories: [],
      categoryColor: "",
      categoryTitle: "",
      category: null,
      error: null,
      competitionListingId: null,
      showCategorySelect: false,
    }
  },
  created() {
    Http.get(this.apiUrl).then((resp) => {
      this.selectableTags = resp.data.tags
      this.changedTags = resp.data.results
      this.results = resp.data.results
      this.preview = resp.data.preview
      this.categoryTitle = resp.data.category_title
      this.categoryColor = resp.data.category_color || ""
      this.competitionListingId = resp.data.competition_listing_id
      this.categories = resp.data.categories
    })
  },
  mounted() {
    this.$modal.show(`shortlist-${this.listingId}`)
  },
  computed: {
    isSSR() {
      // checks if the modal is being used by Slim renderer on the admin's page.
      // in this case this Vue app doesn't have any parent Vue app, so uids would be the same
      return this.$root._uid === this.$parent._uid
    },
    categoryStyle() {
      return `background-color: #${this.categoryColor};`
    },
  },
  methods: {
    onChange(v) {
      this.add(v)
    },
    closeModal() {
      this.$emit("close")
      this.$modal.hide(`shortlist-${this.listingId}`)

      if (this.isSSR) {
        // if the modal is being used on the admin's page which is SSR, it needs to destroy its Vue app instance and all the listeners when it's closed
        this.$destroy()
        this.$el.parentNode.removeChild(this.$el)
      }
    },
    remove(tag) {
      this.changedTags = this.changedTags.filter((item) => item !== tag)
    },
    imageLoaded() {
      this.previewImgLoaded = true
    },
    save() {
      this.saving = true
      this.error = null
      const payload = {
        results: this.changedTags,
      }

      if (this.category) {
        payload.category_id = this.category
      }

      Http.patch(this.apiUrl, payload)
        .then((response) => {
          if (response.status === 200) {
            this.results = this.changedTags

            if (this.isSSR) {
              const shortlistColumn = document.getElementById(
                `competition-listing-results-${this.competitionListingId}`,
              )
              shortlistColumn.innerHTML = ""

              response.data.results.forEach((result) => {
                shortlistColumn.innerHTML =
                  shortlistColumn.innerHTML +
                  `<span class="inline-block border-dashed border-b-2">${result}</span><br />`
              })
            }
          }
        })
        .catch((err) => {
          if (err.response?.data?.errors?.results.length) {
            this.error = err.response.data.errors.results.toString()
          }
        })
        .finally(this.finalize)
    },
    add(tag) {
      this.changedTags = [tag, ...this.changedTags]
    },
    finalize() {
      setTimeout(this.closeModal, closeTimeout)
    },
  },
  components: {
    ShortlistTag,
    Select,
  },
}
</script>

<style>
@import "@/css/tailwind_theme.css";
@reference "tailwindcss";

.shortlist__tag-list {
  min-height: 160px;
}

.shortlist__tag-list ul {
  overflow-y: scroll;
  max-height: 150px;
}

.shortlist__select .vs__open-indicator {
  fill: #201e1e;
}

.shortlist__select .vs__selected-options {
  padding-left: 0;
  flex-wrap: nowrap;
}

.shortlist__select .vs__selected {
  padding-left: 0;
  margin-left: 0;
}

.shortlist__select .vs__search {
  padding-left: 0;
  @apply text-base;
}

.shortlist__select .vs__search:focus {
  @apply text-base;
}

.shortlist__select .vs__actions {
  padding-right: 0;
}

.shortlist__select .vs__dropdown-menu {
  height: 200px !important;
}

.shortlist__select .vs__dropdown-menu li {
  overflow-x: hidden;
  text-overflow: ellipsis;
}

.shortlist__select .vs__dropdown-toggle {
  border: 1px solid #d9d9d9;
  padding: 8px 13px;
}

.shortlist__select .vs__dropdown-option {
  padding-left: 13px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
